// RigSentinel Owner Dashboard V1 — read-only, filterable, paginated account
// table. Row click opens the read-only detail drawer. CSV export mirrors
// the exact filters currently applied. No mutation actions anywhere here.
import React, { useEffect, useState } from "react";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchUsers, exportCsvUrl } from "@/lib/ownerDashboardApi";
import OwnerUserDrawer from "./OwnerUserDrawer";

const ACCESS_OPTIONS = ["", "OWNER", "FREE", "TRIAL", "PRO", "ENTERPRISE", "NO PRO ACCESS"];
const ACTIVITY_OPTIONS = [
  ["", "Any activity"], ["today", "Active today"], ["7d", "Active this week"],
  ["30d", "Active this month"], ["inactive", "Inactive"], ["never", "Never logged in"],
];
const LOGGED_IN_OPTIONS = [["", "Any"], ["true", "Logged in"], ["false", "Never logged in"]];
const FOUNDING_OPTIONS = [["", "Any"], ["true", "Founding 500"], ["false", "Not Founding 500"]];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
}

export default function OwnerUsersTable() {
  const [filters, setFilters] = useState({ search: "", effectiveAccess: "", activity: "", loggedIn: "", founding: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [drawerUserId, setDrawerUserId] = useState(null);

  useEffect(() => {
    fetchUsers({ ...filters, page, pageSize: 25 }).then(setData).catch((e) => setError(e.message));
  }, [filters, page]);

  const setFilter = (key, value) => { setPage(1); setFilters((f) => ({ ...f, [key]: value })); };
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <section className="card-rs p-4" data-testid="owner-users-table-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-[12px] label-up text-[var(--rs-text-3)]">Accounts {data ? `(${data.total})` : ""}</h3>
        <a
          href={exportCsvUrl(filters)}
          data-testid="owner-export-csv"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md font-semibold text-[11px] border"
          style={{ borderColor: "var(--rs-border)", color: "var(--rs-amber)" }}
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </a>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="owner-users-filters">
        <input
          data-testid="owner-filter-search"
          placeholder="Search email or user ID"
          value={filters.search}
          onChange={(e) => setFilter("search", e.target.value)}
          className="input-rs h-8 text-[12px] flex-1 min-w-[180px]"
        />
        <select data-testid="owner-filter-access" value={filters.effectiveAccess} onChange={(e) => setFilter("effectiveAccess", e.target.value)} className="input-rs h-8 text-[12px]">
          {ACCESS_OPTIONS.map((v) => <option key={v} value={v}>{v || "Any access"}</option>)}
        </select>
        <select data-testid="owner-filter-activity" value={filters.activity} onChange={(e) => setFilter("activity", e.target.value)} className="input-rs h-8 text-[12px]">
          {ACTIVITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="owner-filter-loggedin" value={filters.loggedIn} onChange={(e) => setFilter("loggedIn", e.target.value)} className="input-rs h-8 text-[12px]">
          {LOGGED_IN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="owner-filter-founding" value={filters.founding} onChange={(e) => setFilter("founding", e.target.value)} className="input-rs h-8 text-[12px]">
          {FOUNDING_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {error && <p className="text-[12px] text-[var(--rs-danger)]" data-testid="owner-users-error">{error}</p>}
      {!error && !data && <p className="text-[12px] text-[var(--rs-text-3)]">Loading accounts…</p>}

      {data && (
        <>
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-[11.5px] border-collapse" data-testid="owner-users-table">
              <thead>
                <tr className="text-left text-[var(--rs-text-3)] border-b border-[var(--rs-border)]">
                  <th className="py-1.5 pr-3">Email</th>
                  <th className="py-1.5 pr-3">Access</th>
                  <th className="py-1.5 pr-3">Reason</th>
                  <th className="py-1.5 pr-3">Plan</th>
                  <th className="py-1.5 pr-3">Founding</th>
                  <th className="py-1.5 pr-3">Created</th>
                  <th className="py-1.5 pr-3">Last login</th>
                  <th className="py-1.5 pr-3">Activity</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={8} className="py-4 text-center text-[var(--rs-text-3)]">No accounts match these filters.</td></tr>
                )}
                {data.rows.map((r) => (
                  <tr
                    key={r.userId}
                    data-testid={`owner-user-row-${r.userId}`}
                    onClick={() => setDrawerUserId(r.userId)}
                    className="border-b border-[var(--rs-border)] cursor-pointer hover:bg-[rgba(255,179,0,0.06)]"
                  >
                    <td className="py-1.5 pr-3 text-[var(--rs-text)]">{r.email}</td>
                    <td className="py-1.5 pr-3 font-mono-rs" style={{ color: r.effectiveAccess === "OWNER" ? "var(--rs-amber)" : "var(--rs-text-2)" }}>{r.effectiveAccess}</td>
                    <td className="py-1.5 pr-3 text-[var(--rs-text-3)]">{r.entitlementReason}</td>
                    <td className="py-1.5 pr-3 text-[var(--rs-text-2)]">{r.plan}</td>
                    <td className="py-1.5 pr-3 text-[var(--rs-text-2)]">{r.founding500 ? "Yes" : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--rs-text-3)]">{fmtDate(r.accountCreated)}</td>
                    <td className="py-1.5 pr-3 text-[var(--rs-text-3)]">{fmtDate(r.lastLogin)}</td>
                    <td className="py-1.5 pr-3 text-[var(--rs-text-3)]">{r.activityState}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-[11px] text-[var(--rs-text-3)]">Page {data.page} of {totalPages}</span>
            <div className="flex gap-2">
              <button data-testid="owner-users-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn-ghost h-8 px-2 rounded-md disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button data-testid="owner-users-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="btn-ghost h-8 px-2 rounded-md disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      <OwnerUserDrawer userId={drawerUserId} open={!!drawerUserId} onClose={() => setDrawerUserId(null)} />
    </section>
  );
}
